import { UseMutateAsyncFunction, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiErrorResponseType } from "../../custom-types/api-error-response-type";
import { LoadBidFormVals, LorryBidFormVals } from "../../custom-types/bid-form";
import API from "../const";
import { QueryNames } from "./queryNames";
import { getAuthHeader } from "../utils/getAuthHeader";
import { toast } from "react-toastify";
import { getAuthIdParam } from "../../methods/getAuthIdParam";
import useVahakHttpClient from "../../hooks/useVahakHttpClient";
import { useVahakMutationQuery } from "../../hooks/useVahakQueryClient";

interface MutationHookParams {
    invalidateOnSuccess?: boolean;
}

interface PostLoadParams {
    companyId?: number | string;
    loadVals: LorryBidFormVals;
    isReposted: boolean;
}

export function usePostLoad(hookParam?: MutationHookParams) {
    const queryClient = useQueryClient();

    const { mutateAsync } = useMutation({ mutationFn: ({ companyId, loadVals, isReposted }: PostLoadParams) =>
            fetch(API.POST_LOAD + getAuthIdParam(), {
                method: "POST",
                headers: { ...getAuthHeader() },
                body: JSON.stringify({
                    quantity: parseFloat(String(loadVals.qty)),
                    capacity: parseFloat(String(loadVals.lorryCapacity)),
                    length: parseFloat(String(loadVals.len || 0.0)),
                    breadth: parseFloat(String(loadVals.breadth || 0.0)),
                    height: parseFloat(String(loadVals.height || 0.0)),
                    expected_amount: parseFloat(String(loadVals.amount)),
                    material_type: loadVals.material,
                    payment_type: Number(loadVals.paymentType),
                    is_odc: !!loadVals.isOdc,
                    remarks: loadVals.remarks,
                    company_id: Number(companyId),
                    lorry_id: loadVals.lorryTypeId,
                    // source_id: loadVals.src,
                    // destination_id: loadVals.dest,
                    source_place_id: loadVals.srcPlaceId,
                    destination_place_id: loadVals.destPlaceId,
                    is_reposted: isReposted || null,
                    expected_type: loadVals.isFixedPrice ? 0 : 0, //per tonne is not in used, so passing 0 which is Fixed
                    total_tyres: loadVals.totalTyres || 0,
                    body_type: loadVals.bodyType || 0,
                    size: loadVals.size || 0,
                    advance_payment_percentage: parseInt(String(loadVals.advancePaymentPercentage || 0), 10),
                    size_text: loadVals.sizeText,
                    loading_timestamp: loadVals.loadingTimestamp,
                    load_ghosting_penalty_deduction: loadVals.loadGhostingPenaltyDeduction,
                    spot_load_auction_enabled: true,
                    do_dup_check: true,
                    is_verified: loadVals?.isVerified,
                    verification_source: loadVals?.verificationSource,
                    stops: loadVals?.stops
                })
            }), onError: (err) => {
                console.log("err posting", err);
            },
            onMutate: () => queryClient.cancelQueries(QueryNames.loadsForCurrentUser),
            onSuccess: (res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                }
                hookParam?.invalidateOnSuccess && queryClient.invalidateQueries(QueryNames.loadsForCurrentUser);
            } });

    return { mutateAsync };
}

interface PostLorryParams {
    companyId?: number | string;
    lorryVals: LoadBidFormVals;
}

export function usePostLorry(hookParam?: MutationHookParams) {
    const queryClient = useQueryClient();

    const { mutateAsync } = useMutation({ mutationFn: ({ companyId, lorryVals }: PostLorryParams) =>
            fetch(API.POST_LORRY + getAuthIdParam(), {
                method: "POST",
                headers: { ...getAuthHeader() },
                body: JSON.stringify({
                    company_id: Number(companyId),
                    number: lorryVals.lorryNum?.toLowerCase().replaceAll(" ", ""),
                    capacity: lorryVals.lorryCapacity,
                    type: lorryVals.lorryType,
                    route_ids: lorryVals.routeIds,
                    // documents: lorryVals.documents || null,
                    current_place_id: lorryVals.currentPlaceID,
                    current_city_id: lorryVals.currentCityId,
                    size: lorryVals.lorrySize,
                    total_tyres: lorryVals.lorryTyres,
                    body_type: lorryVals.lorryBodyType
                })
            }), onMutate: () => queryClient.cancelQueries(QueryNames.lorriesForCurrentUser),
            onSuccess: (res) => {
                if (res.status === 401) {
                    queryClient.invalidateQueries(QueryNames.validateToken);
                }
                hookParam?.invalidateOnSuccess && queryClient.invalidateQueries(QueryNames.lorriesForCurrentUser);
            } });

    return { mutateAsync };
}

interface ReactivateLorryBody {
    lorry_id: number;
    current_place_id: string;
}

interface ReactivateLorryParams {
    lorryId: number;
    currentPlaceID: string;
}

interface ReactivateLorrySuccessResponse {
    data: { message: string };
}

export function useReactivateLorry() {
    const { POST } = useVahakHttpClient();

    const reactivateLorry = ({ lorryId, currentPlaceID }: ReactivateLorryParams) => {
        const payload = { lorry_id: lorryId, current_place_id: currentPlaceID };
        return POST<ReactivateLorryBody, ReactivateLorrySuccessResponse>(API.REACTIVATE_LORRY, payload);
    };

    return useVahakMutationQuery<ReactivateLorrySuccessResponse, ApiErrorResponseType, ReactivateLorryParams>(
        QueryNames.userLorries.reactivateLorry,
        reactivateLorry
    );
}

async function handlePostResponse<T>(mutateFn: UseMutateAsyncFunction<Response, unknown, T, void>, postingParams: T) {
    try {
        const postedItemRes = await mutateFn(postingParams);
        if (postedItemRes.status === 200) {
            const postedLoadJson = await postedItemRes.json();
            return { postedItemId: postedLoadJson.id, success: true, data: postedLoadJson };
        } else if (postedItemRes.status === 208) {
            const duplicateLoadJson = await postedItemRes.json();
            return {
                postedItemId: duplicateLoadJson.data?.id,
                success: false,
                data: duplicateLoadJson
            };
        } else {
            const error = { message: "An Error Occurred. Please try again after some time." };
            throw error;
        }
    } catch (e: any) {
        toast.error(e.message || "An error occurred, please try again");
    }
    return { success: false };
}

export async function handleLorryPostResponse(
    mutateFn: UseMutateAsyncFunction<Response, unknown, PostLorryParams, void>,
    postingParams: PostLorryParams
) {
    const { postedItemId, success } = await handlePostResponse(mutateFn, postingParams);
    return { lorryId: postedItemId, success };
}

export async function handleLorryReactivateResponse(
    mutateFn: UseMutateAsyncFunction<Response, unknown, ReactivateLorryParams, void>,
    postingParams: ReactivateLorryParams
) {
    const { success } = await handlePostResponse(mutateFn, postingParams);
    return { success };
}

export async function handleLoadPostResponse(
    mutateFn: UseMutateAsyncFunction<Response, unknown, PostLoadParams, void>,
    postingParams: PostLoadParams
) {
    const { postedItemId, success, data } = await handlePostResponse(mutateFn, postingParams);
    return { loadId: postedItemId, success, data };
}
